import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import { ROUTES } from '@/lib/constants';
import { buildersDataByAddress } from '@/lib/constants/hyperTrackerBuildersConstants';
import { segmentLabelToId } from '@/lib/constants/hyperTrackerConstants';
import { LEADERBOARDS } from '@/state/hypertracker/hypertrackerTypes';
import { shortenString } from '@/lib/helpers/helpers';
import { SegmentId } from '@/lib/types/hypertrackerTypes';

export const HYPERTRACKER_DEFAULT_TITLE = 'HyperTracker | Real-Time Hyperliquid Insights by CMM';

const STATIC_HYPERTRACKER_TITLES: Record<string, string> = {
    [ROUTES.HYPERTRACKER]: 'Hyperliquid Intelligence | HyperTracker',
    [ROUTES.HYPERTRACKER_HEATMAP]: 'Position Heatmap | Hyperliquid Cohorts | HyperTracker',
    [ROUTES.HYPERTRACKER_FAVORITES]: 'Favourites | Wallet Watchlist | HyperTracker',
    [ROUTES.HYPERTRACKER_CONNECTIONS]: 'Connections | Link Wallets & Exchanges | HyperTracker',
    [ROUTES.HYPERTRACKER_PERPS]: 'Perps Markets | HyperTracker',
    [ROUTES.HYPERTRACKER_STATS]: 'Stats | Hyperliquid Network Metrics | HyperTracker',
    [ROUTES.HYPERTRACKER_BUILDER]: 'Builders | Revenue Analytics | HyperTracker',
    [ROUTES.HYPERTRACKER_LEADERBOARDS]: 'Leaderboards | Hyperliquid Rankings | HyperTracker',
    [ROUTES.HYPERTRACKER_API]: 'API Access | Hyperliquid Data API | HyperTracker',
    [ROUTES.HYPERTRACKER_API_DASHBOARD]: 'API Dashboard | Usage & Keys | HyperTracker',
    [ROUTES.HYPERTRACKER_ALERTS]: 'Alerts | Wallet Notifications | HyperTracker',
    [ROUTES.HYPERTRACKER_PRICING]: 'Pricing | API Plans | HyperTracker',
    [ROUTES.HYPERTRACKER_MARKET_RADAR]:
        'Market Radar | Liquidation & Order Clusters | HyperTracker',
    [ROUTES.HYPERTRACKER_LIQUIDATIONS]: 'Liquidations | Hyperliquid Risk Map | HyperTracker',
    [ROUTES.HYPERTRACKER_ALPHAFEED]: 'AlphaFeed | Hyperliquid Trader Discovery | Coming Soon',
};

const LEADERBOARD_TITLES: Partial<Record<LEADERBOARDS, string>> = {
    [LEADERBOARDS.PNL_PERP_ONLY]: 'Perp PNL | Leaderboard | HyperTracker',
    [LEADERBOARDS.ALL_PNL]: 'Total PNL | Leaderboard | HyperTracker',
    [LEADERBOARDS.AVG_DAILY_PERP_PNL]: '30D Avg Perp PNL | Leaderboard | HyperTracker',
    [LEADERBOARDS.BIGGEST_POSITIONS]: 'Biggest Positions | Leaderboard | HyperTracker',
    [LEADERBOARDS.TOP_CLAIMED]: 'Top Claimed | Leaderboard | HyperTracker',
    [LEADERBOARDS.REFERRALS]: 'Referrals | Leaderboard | HyperTracker',
    [LEADERBOARDS.TOP_HYPE_HOLDERS]: 'Top HYPE Holders | Leaderboard | HyperTracker',
    [LEADERBOARDS.RICH_LIST]: 'Rich List | Leaderboard | HyperTracker',
    [LEADERBOARDS.VAULT_LEADERBOARDS]: 'Vaults | Leaderboard | HyperTracker',
};

const LEADERBOARD_SLUGS: Partial<Record<string, LEADERBOARDS>> = {
    'pnl-perp-only': LEADERBOARDS.PNL_PERP_ONLY,
    'perp-pnl': LEADERBOARDS.PNL_PERP_ONLY,
    'all-pnl': LEADERBOARDS.ALL_PNL,
    'avg-daily-perp-pnl': LEADERBOARDS.AVG_DAILY_PERP_PNL,
    'avg-perp-pnl-30d': LEADERBOARDS.AVG_DAILY_PERP_PNL,
    'biggest-positions': LEADERBOARDS.BIGGEST_POSITIONS,
    'top-claimed': LEADERBOARDS.TOP_CLAIMED,
    referrals: LEADERBOARDS.REFERRALS,
    'top-hype-holders': LEADERBOARDS.TOP_HYPE_HOLDERS,
    'rich-list': LEADERBOARDS.RICH_LIST,
    vaults: LEADERBOARDS.VAULT_LEADERBOARDS,
    'vault-leaderboards': LEADERBOARDS.VAULT_LEADERBOARDS,
};

const SEGMENT_TITLES: Record<SegmentId, string> = {
    [SegmentId.Leviathan]: 'Leviathan',
    [SegmentId.Shrimp]: 'Shrimp',
    [SegmentId.Fish]: 'Fish',
    [SegmentId.Dolphin]: 'Dolphin',
    [SegmentId.TidalWhale]: 'Tidal Whale',
    [SegmentId.Whale]: 'Whale',
    [SegmentId.SmallWhale]: 'Small Whale',
    [SegmentId.ApexPredator]: 'Apex Predator',
    [SegmentId.MoneyPrinter]: 'Money Printer',
    [SegmentId.SmartMoney]: 'Smart Money',
    [SegmentId.ConsistentGrinder]: 'Consistent Grinder',
    [SegmentId.HumbleEarner]: 'Humble Earner',
    [SegmentId.ExitLiquidity]: 'Exit Liquidity',
    [SegmentId.SemiRekt]: 'Semi-Rekt',
    [SegmentId.FullRekt]: 'Full Rekt',
    [SegmentId.GigaRekt]: 'Giga Rekt',
};

const normalizePath = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

const titleCase = (value: string) =>
    value
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

const getReadableSegmentName = (segment?: string) => {
    if (!segment) return null;

    const segmentId = Number(segment) || segmentLabelToId[segment];
    const segmentTitle = SEGMENT_TITLES[segmentId];

    return segmentTitle || titleCase(segment);
};

const getBuilderName = (builder?: string) => {
    if (!builder) return null;

    return buildersDataByAddress?.[builder.toLowerCase()]?.name || shortenString(builder);
};

const getLeaderboardTitle = (leaderboard?: string) => {
    if (!leaderboard) return STATIC_HYPERTRACKER_TITLES[ROUTES.HYPERTRACKER_LEADERBOARDS];

    const leaderboardType = LEADERBOARD_SLUGS[leaderboard];
    if (!leaderboardType) return HYPERTRACKER_DEFAULT_TITLE;

    return LEADERBOARD_TITLES[leaderboardType] || HYPERTRACKER_DEFAULT_TITLE;
};

interface HypertrackerTitleParams {
    segment?: string;
    wallet?: string;
    coin?: string;
    builder?: string;
    leaderboard?: string;
}

export const getHypertrackerTitle = (pathname: string, params: HypertrackerTitleParams = {}) => {
    const normalizedPathname = normalizePath(pathname);

    if (normalizedPathname === `${ROUTES.HYPERTRACKER}/${ROUTES.HYPERTRACKER_PURCHASE}`) {
        return 'Checkout | API Plan Purchase | HyperTracker';
    }

    if (params.segment && normalizedPathname.startsWith(`${ROUTES.HYPERTRACKER_SEGMENTS}/`)) {
        const segmentName = getReadableSegmentName(params.segment);
        return segmentName
            ? `${segmentName} | Cohort Intelligence | HyperTracker`
            : HYPERTRACKER_DEFAULT_TITLE;
    }

    if (params.wallet && normalizedPathname.startsWith(`${ROUTES.HYPERTRACKER_WALLET}/`)) {
        return `${shortenString(params.wallet, 6, 3)} | Wallet Analytics | HyperTracker`;
    }

    if (params.coin && normalizedPathname.startsWith(`${ROUTES.HYPERTRACKER_PERPS}/`)) {
        return `${params.coin.toUpperCase()} | Perps Analytics | HyperTracker`;
    }

    if (
        params.builder &&
        normalizedPathname.startsWith(`${ROUTES.HYPERTRACKER_BUILDER_PERSONAL}/`)
    ) {
        const builderName = getBuilderName(params.builder);
        return builderName
            ? `${builderName} | Builder Profile | HyperTracker`
            : HYPERTRACKER_DEFAULT_TITLE;
    }

    if (normalizedPathname.startsWith(`${ROUTES.HYPERTRACKER_LEADERBOARDS}/`)) {
        return getLeaderboardTitle(params.leaderboard);
    }

    return STATIC_HYPERTRACKER_TITLES[normalizedPathname] || HYPERTRACKER_DEFAULT_TITLE;
};

const useHypertrackerTitle = () => {
    const location = useLocation();
    const params = useParams();

    const title = useMemo(
        () => getHypertrackerTitle(location.pathname, params),
        [location.pathname, params],
    );

    useEffect(() => {
        document.title = title;
    }, [title]);
};

export const useHypertrackerTitleOverride = (title: string | null) => {
    useEffect(() => {
        if (!title) return;
        document.title = title;
    }, [title]);
};

export default useHypertrackerTitle;
